import type {
  Member,
  PertEdge,
  PertNode,
  Project,
  ProjectSettings,
  ProjectMembership,
  ProjectInvite,
  ProjectRole,
  GlobalRole,
} from '../types';
import type { ProjectDataPayload } from '../types/projectData';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';
import { getAuthToken, refreshAccessToken } from '../store';

interface AuthorizedRequestOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | object | null;
}

export interface BackendNodeRecord {
  id: string;
  name: string;
  description?: string | null;
  projectId: string;
  data?: unknown;
  createdAt?: string;
  updatedAt?: string;
}

export interface BackendEdgeRecord {
  id: string;
  projectId: string;
  fromNodeId: string;
  toNodeId: string;
  data?: unknown;
  createdAt?: string;
  updatedAt?: string;
}

export interface BackendMemberRecord {
  id: string;
  projectId: string;
  name: string;
  email: string;
  role: string;
  data?: unknown;
  createdAt?: string;
  updatedAt?: string;
}

export interface BackendProjectResponse {
  id: string;
  name: string;
  description?: string | null;
  clientId?: string | null;
  startDate: string;
  endDate?: string | null;
  settings?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  nodes?: BackendNodeRecord[];
  edges?: BackendEdgeRecord[];
  members?: BackendMemberRecord[];
  currentUserRole?: ProjectRole;
  memberships?: Array<{ role: ProjectRole }>;
}

export interface ProjectDataResponse {
  projectId: string;
  payload: ProjectDataPayload;
  updatedAt?: string | null;
}

interface BackendMembershipRecord {
  id: string;
  projectId: string;
  userId: string;
  role: ProjectRole;
  status: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    globalRole: GlobalRole | string;
  };
  invitedBy?: {
    id: string;
    email: string;
    name: string | null;
  } | null;
}

interface BackendInviteRecord {
  id: string;
  projectId: string;
  email: string;
  role: ProjectRole;
  status: string;
  token: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string | null;
  acceptedAt?: string | null;
  project?: {
    id: string;
    name: string;
  };
  invitedBy: {
    id: string;
    email: string;
    name: string | null;
  };
}

export interface CreateBackendProjectPayload {
  name: string;
  description: string;
  startDate?: Date | string;
  clientId?: string;
}

export interface NodePayload {
  id?: string;
  name?: string;
  description?: string | null;
  data?: Partial<PertNode>;
}

export interface EdgePayload {
  id?: string;
  fromNodeId?: string;
  toNodeId?: string;
  data?: Partial<PertEdge>;
}

export interface MemberPayload {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  data?: Partial<Member>;
}

const serializeBody = (body: AuthorizedRequestOptions['body']): BodyInit | null | undefined => {
  if (
    body == null ||
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof URLSearchParams
  ) {
    return body as BodyInit | null | undefined;
  }
  return JSON.stringify(body);
};

const handleResponse = async (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'message' in payload
        ? (payload as { message: string }).message
        : response.statusText || 'Erreur serveur';
    throw new Error(message);
  }

  return payload;
};

const authorizedRequest = async <T>(
  path: string,
  options: AuthorizedRequestOptions = {},
  _retry = true,
): Promise<T> => {
  const headers = new Headers(options.headers || {});

  if (options.body !== undefined && options.body !== null && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getAuthToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    method: options.method ?? 'GET',
    body: serializeBody(options.body),
    credentials: 'include',
  });

  // ── Intercepteur 401 : tente un refresh puis rejoue la requête une fois ──
  if (response.status === 401 && _retry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return authorizedRequest<T>(path, options, false);
    }
    // Refresh impossible — l'utilisateur doit se reconnecter
    // On vide le state via un reload propre pour éviter les données stale
    window.location.href = '/';
    throw new Error('Session expirée — veuillez vous reconnecter');
  }

  return handleResponse(response) as Promise<T>;
};

const fallbackSettings: ProjectSettings = {
  workingHoursPerDay: 8,
  workingDaysPerWeek: 5,
  currency: 'EUR',
  timezone: 'Europe/Paris',
  dateFormat: 'dd/MM/yyyy',
  includeWeekends: true,
  holidays: [],
};

const normalizeDate = (value?: string | Date | null): Date => {
  if (!value) return new Date();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const normalizeSettings = (settings: unknown): ProjectSettings => {
  if (!settings || typeof settings !== 'object') {
    return fallbackSettings;
  }
  const partial = settings as Partial<ProjectSettings>;
  return {
    ...fallbackSettings,
    ...partial,
    holidays: Array.isArray(partial.holidays) ? partial.holidays : [],
  };
};

const mapBackendNode = (record: BackendNodeRecord): PertNode => {
  const raw = (record.data as Partial<PertNode>) || {};
  return {
    id: record.id,
    name: raw.name ?? record.name ?? 'Nouvelle tâche',
    description: raw.description ?? record.description ?? '',
    x: raw.x ?? 100,
    y: raw.y ?? 100,
    earlyStart: raw.earlyStart ?? 0,
    earlyFinish: raw.earlyFinish ?? 0,
    lateStart: raw.lateStart ?? 0,
    lateFinish: raw.lateFinish ?? 0,
    slack: raw.slack ?? 0,
    isCritical: raw.isCritical ?? false,
    type: raw.type ?? 'task',
    assignedTo: raw.assignedTo ?? [],
    resources: raw.resources ?? [],
    progress: raw.progress ?? 0,
    createdAt: normalizeDate(record.createdAt),
    updatedAt: normalizeDate(record.updatedAt),
  };
};

const mapBackendEdge = (record: BackendEdgeRecord): PertEdge => {
  const raw = (record.data as Partial<PertEdge>) || {};
  return {
    id: record.id,
    from: raw.from ?? record.fromNodeId,
    to: raw.to ?? record.toNodeId,
    taskName: raw.taskName ?? 'Nouvelle liaison',
    duration: raw.duration ?? 1,
    isCritical: raw.isCritical ?? false,
    dependencyType: raw.dependencyType ?? 'FS',
    lag: raw.lag ?? 0,
    description: raw.description ?? '',
    createdAt: normalizeDate(record.createdAt),
    updatedAt: normalizeDate(record.updatedAt),
  };
};

const mapBackendMember = (record: BackendMemberRecord): Member => {
  const raw = (record.data as Partial<Member>) || {};
  return {
    id: record.id,
    name: record.name ?? raw.name ?? '',
    email: record.email ?? raw.email ?? '',
    role: record.role ?? raw.role ?? '',
    avatar: raw.avatar,
    department: raw.department,
    skills: raw.skills ?? [],
  };
};

const mapBackendMembership = (record: BackendMembershipRecord): ProjectMembership => ({
  id: record.id,
  projectId: record.projectId,
  userId: record.userId,
  role: record.role ?? 'VIEWER',
  status: (record.status as ProjectMembership['status']) ?? 'ACTIVE',
  createdAt: normalizeDate(record.createdAt),
  updatedAt: normalizeDate(record.updatedAt),
  user: {
    id: record.user.id,
    email: record.user.email,
    name: record.user.name ?? null,
    globalRole: (record.user.globalRole as ProjectMembership['user']['globalRole']) ?? 'MEMBER',
  },
  invitedBy: record.invitedBy
    ? {
        id: record.invitedBy.id,
        email: record.invitedBy.email,
        name: record.invitedBy.name ?? null,
      }
    : undefined,
});

const mapBackendInvite = (record: BackendInviteRecord): ProjectInvite => ({
  id: record.id,
  projectId: record.projectId,
  email: record.email,
  role: record.role ?? 'VIEWER',
  status: (record.status as ProjectInvite['status']) ?? 'PENDING',
  token: record.token,
  createdAt: normalizeDate(record.createdAt),
  updatedAt: normalizeDate(record.updatedAt),
  expiresAt: record.expiresAt ? normalizeDate(record.expiresAt) : undefined,
  acceptedAt: record.acceptedAt ? normalizeDate(record.acceptedAt) : undefined,
  project: record.project
    ? {
        id: record.project.id,
        name: record.project.name,
      }
    : undefined,
  invitedBy: {
    id: record.invitedBy.id,
    email: record.invitedBy.email,
    name: record.invitedBy.name ?? null,
  },
});

export const transformBackendProject = (record: BackendProjectResponse): Project => ({
  id: record.id,
  name: record.name,
  description: record.description ?? '',
  clientId: record.clientId ?? undefined,
  nodes: (record.nodes ?? []).map(mapBackendNode),
  edges: (record.edges ?? []).map(mapBackendEdge),
  members: (record.members ?? []).map(mapBackendMember),
  startDate: normalizeDate(record.startDate),
  endDate: record.endDate ? new Date(record.endDate) : undefined,
  settings: normalizeSettings(record.settings),
  createdAt: normalizeDate(record.createdAt),
  updatedAt: normalizeDate(record.updatedAt),
  currentUserRole: record.currentUserRole ?? 'VIEWER',
});

export const fetchBackendProjects = async (): Promise<BackendProjectResponse[]> =>
  authorizedRequest<BackendProjectResponse[]>('/projects');

export const fetchBackendProject = async (projectId: string): Promise<BackendProjectResponse> =>
  authorizedRequest<BackendProjectResponse>(`/projects/${projectId}`);

export const createBackendProject = async (
  payload: CreateBackendProjectPayload
): Promise<BackendProjectResponse> => {
  const startDate =
    payload.startDate instanceof Date
      ? payload.startDate.toISOString()
      : payload.startDate ?? new Date().toISOString();

  return authorizedRequest<BackendProjectResponse>('/projects', {
    method: 'POST',
    body: {
      ...payload,
      startDate,
    },
  });
};

export interface UpdateBackendProjectPayload {
  name?: string;
  description?: string | null;
  clientId?: string | null;
  startDate?: Date | string;
  endDate?: Date | string | null;
  settings?: Record<string, unknown>;
}

export const updateBackendProject = async (
  projectId: string,
  payload: UpdateBackendProjectPayload,
): Promise<BackendProjectResponse> => {
  const body: Record<string, unknown> = { ...payload };
  if (payload.startDate instanceof Date) body.startDate = payload.startDate.toISOString();
  if (payload.endDate instanceof Date)   body.endDate   = payload.endDate.toISOString();
  return authorizedRequest<BackendProjectResponse>(`/projects/${projectId}`, {
    method: 'PATCH',
    body,
  });
};

export const deleteBackendProject = async (projectId: string): Promise<void> =>
  authorizedRequest<void>(`/projects/${projectId}`, { method: 'DELETE' });

export type SampleScenario = 'success' | 'partial' | 'failed' | 'day5';

export const seedSampleProject = async (
  scenario: SampleScenario,
  count = 3
): Promise<{ projectIds: string[] }> =>
  authorizedRequest<{ projectIds: string[] }>(`/projects/sample-data`, {
    method: 'POST',
    body: { scenario, count },
  });

export const createBackendNode = async (projectId: string, payload: NodePayload): Promise<BackendNodeRecord> =>
  authorizedRequest<BackendNodeRecord>(`/projects/${projectId}/nodes`, {
    method: 'POST',
    body: payload,
  });

export const updateBackendNode = async (
  projectId: string,
  nodeId: string,
  payload: NodePayload
): Promise<BackendNodeRecord> =>
  authorizedRequest<BackendNodeRecord>(`/projects/${projectId}/nodes/${nodeId}`, {
    method: 'PUT',
    body: payload,
  });

export const deleteBackendNode = async (projectId: string, nodeId: string): Promise<void> =>
  authorizedRequest<void>(`/projects/${projectId}/nodes/${nodeId}`, {
    method: 'DELETE',
  });

export const createBackendEdge = async (projectId: string, payload: EdgePayload): Promise<BackendEdgeRecord> =>
  authorizedRequest<BackendEdgeRecord>(`/projects/${projectId}/edges`, {
    method: 'POST',
    body: payload,
  });

export const updateBackendEdge = async (
  projectId: string,
  edgeId: string,
  payload: EdgePayload
): Promise<BackendEdgeRecord> =>
  authorizedRequest<BackendEdgeRecord>(`/projects/${projectId}/edges/${edgeId}`, {
    method: 'PUT',
    body: payload,
  });

export const deleteBackendEdge = async (projectId: string, edgeId: string): Promise<void> =>
  authorizedRequest<void>(`/projects/${projectId}/edges/${edgeId}`, {
    method: 'DELETE',
  });

export const createBackendMember = async (
  projectId: string,
  payload: MemberPayload
): Promise<BackendMemberRecord> =>
  authorizedRequest<BackendMemberRecord>(`/projects/${projectId}/members`, {
    method: 'POST',
    body: payload,
  });

export const updateBackendMember = async (
  projectId: string,
  memberId: string,
  payload: MemberPayload
): Promise<BackendMemberRecord> =>
  authorizedRequest<BackendMemberRecord>(`/projects/${projectId}/members/${memberId}`, {
    method: 'PUT',
    body: payload,
  });

export const deleteBackendMember = async (projectId: string, memberId: string): Promise<void> =>
  authorizedRequest<void>(`/projects/${projectId}/members/${memberId}`, {
    method: 'DELETE',
  });

export const fetchProjectData = async (projectId: string): Promise<ProjectDataPayload> => {
  const response = await authorizedRequest<ProjectDataResponse>(`/projects/${projectId}/data`);
  return response.payload ?? {};
};

export const updateProjectData = async (
  projectId: string,
  payload: ProjectDataPayload
): Promise<ProjectDataResponse> =>
  authorizedRequest(`/projects/${projectId}/data`, {
    method: 'PUT',
    body: { payload },
  });

// ============================================================================
// PROJECT SHARING API
// ============================================================================

interface ProjectSharesResponse {
  memberships: BackendMembershipRecord[];
  invites: BackendInviteRecord[];
}

interface CreateShareResponse {
  membership?: BackendMembershipRecord;
  invite?: BackendInviteRecord;
}

interface AcceptInviteResponse {
  membership: BackendMembershipRecord;
  project: BackendProjectResponse;
}

export const fetchProjectShares = async (
  projectId: string
): Promise<{ memberships: ProjectMembership[]; invites: ProjectInvite[] }> => {
  const response = await authorizedRequest<ProjectSharesResponse>(`/projects/${projectId}/shares`);
  return {
    memberships: (response.memberships ?? []).map(mapBackendMembership),
    invites: (response.invites ?? []).map(mapBackendInvite),
  };
};

export const createProjectShare = async (
  projectId: string,
  payload: { email: string; role: ProjectRole }
): Promise<{ membership?: ProjectMembership; invite?: ProjectInvite }> => {
  const response = await authorizedRequest<CreateShareResponse>(`/projects/${projectId}/shares`, {
    method: 'POST',
    body: payload,
  });
  return {
    membership: response.membership ? mapBackendMembership(response.membership) : undefined,
    invite: response.invite ? mapBackendInvite(response.invite) : undefined,
  };
};

export const updateProjectMembershipRole = async (
  projectId: string,
  membershipId: string,
  updates: Partial<{ role: ProjectRole; status: ProjectMembership['status'] }>
): Promise<ProjectMembership> => {
  const response = await authorizedRequest<{ membership: BackendMembershipRecord }>(
    `/projects/${projectId}/shares/memberships/${membershipId}`,
    {
      method: 'PATCH',
      body: updates,
    }
  );
  return mapBackendMembership(response.membership);
};

export const removeProjectMembership = async (projectId: string, membershipId: string): Promise<void> =>
  authorizedRequest<void>(`/projects/${projectId}/shares/memberships/${membershipId}`, {
    method: 'DELETE',
  });

export const revokeProjectInvite = async (
  projectId: string,
  inviteId: string
): Promise<ProjectInvite> => {
  const response = await authorizedRequest<{ invite: BackendInviteRecord }>(
    `/projects/${projectId}/shares/invites/${inviteId}/revoke`,
    {
      method: 'PATCH',
    }
  );
  return mapBackendInvite(response.invite);
};

export const acceptProjectInvite = async (
  token: string
): Promise<{ membership: ProjectMembership; project: Project }> => {
  const response = await authorizedRequest<AcceptInviteResponse>(`/projects/invites/accept`, {
    method: 'POST',
    body: { token },
  });

  return {
    membership: mapBackendMembership(response.membership),
    project: transformBackendProject(response.project),
  };
};

export const fetchMyProjectInvites = async (): Promise<ProjectInvite[]> => {
  const response = await authorizedRequest<{ invites: BackendInviteRecord[] }>(
    '/projects/invites/pending'
  );
  return (response.invites ?? []).map(mapBackendInvite);
};
